import { Chart } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';

interface HeatPumpCostChartProps {
  location: string;
  geofips: number;
}

interface HeatPumpData {
  year: number;
  hvac_cost: number;
  hpwh_cost: number;
  california_hvac_cost: number;
  california_hpwh_cost: number;
}

export const HeatPumpCostChart: React.FC<HeatPumpCostChartProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<HeatPumpData[]>([]);

  useEffect(() => {
    const fetchHeatPumpData = async () => {
      if (!location || !geofips) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch data for the selected location
        const { data: locationData, error: locationError } = await supabaseClient
          .from('heat_pumps')
          .select('year, system, project_cost_per_unit_usd')
          .eq('geo_fips', geofips)
          .in('system', ['HVAC', 'HPWH'])
          .order('year', { ascending: true });

        if (locationError) {
          console.error('Error fetching location heat pump data:', locationError);
          throw locationError;
        }

        // Fetch California average data
        const { data: californiaData, error: californiaError } = await supabaseClient
          .from('heat_pumps')
          .select('year, system, project_cost_per_unit_usd')
          .eq('geo_fips', 6) // California
          .in('system', ['HVAC', 'HPWH'])
          .order('year', { ascending: true });

        if (californiaError) {
          console.error('Error fetching California heat pump data:', californiaError);
          throw californiaError;
        }

        // Process location data
        const locationByYear = new Map<number, { hvac: number; hpwh: number }>();
        locationData?.forEach(record => {
          if (!locationByYear.has(record.year)) {
            locationByYear.set(record.year, { hvac: 0, hpwh: 0 });
          }
          if (record.system === 'HVAC') {
            locationByYear.get(record.year)!.hvac = record.project_cost_per_unit_usd || 0;
          } else if (record.system === 'HPWH') {
            locationByYear.get(record.year)!.hpwh = record.project_cost_per_unit_usd || 0;
          }
        });

        // Process California data
        const californiaByYear = new Map<number, { hvac: number; hpwh: number }>();
        californiaData?.forEach(record => {
          if (!californiaByYear.has(record.year)) {
            californiaByYear.set(record.year, { hvac: 0, hpwh: 0 });
          }
          if (record.system === 'HVAC') {
            californiaByYear.get(record.year)!.hvac = record.project_cost_per_unit_usd || 0;
          } else if (record.system === 'HPWH') {
            californiaByYear.get(record.year)!.hpwh = record.project_cost_per_unit_usd || 0;
          }
        });

        // Combine data
        const allYears = Array.from(new Set([
          ...Array.from(locationByYear.keys()),
          ...Array.from(californiaByYear.keys())
        ])).sort();

        const processedData: HeatPumpData[] = allYears.map(year => {
          const locationYear = locationByYear.get(year) || { hvac: 0, hpwh: 0 };
          const californiaYear = californiaByYear.get(year) || { hvac: 0, hpwh: 0 };

          return {
            year,
            hvac_cost: locationYear.hvac,
            hpwh_cost: locationYear.hpwh,
            california_hvac_cost: californiaYear.hvac,
            california_hpwh_cost: californiaYear.hpwh
          };
        });

        setData(processedData);

        // Create chart
        if (chartRef.current) {
          const ctx = chartRef.current.getContext('2d');
          if (ctx) {
            if (chartInstance.current) {
              chartInstance.current.destroy();
            }

            const years = processedData.map(d => d.year);
            const locationHVAC = processedData.map(d => d.hvac_cost);
            const locationHPWH = processedData.map(d => d.hpwh_cost);
            const californiaHVAC = processedData.map(d => d.california_hvac_cost);
            const californiaHPWH = processedData.map(d => d.california_hpwh_cost);

            chartInstance.current = new Chart(ctx, {
              type: 'line',
              data: {
                labels: years,
                datasets: [
                  {
                    label: `${location} - HVAC`,
                    data: locationHVAC,
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderWidth: 3,
                    fill: false,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    tension: 0.4
                  },
                  {
                    label: `California - HVAC`,
                    data: californiaHVAC,
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderWidth: 2,
                    borderDash: [5, 5],
                    fill: false,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    tension: 0.4
                  },
                  {
                    label: `${location} - HPWH`,
                    data: locationHPWH,
                    borderColor: '#ef4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    borderWidth: 3,
                    fill: false,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    tension: 0.4
                  },
                  {
                    label: `California - HPWH`,
                    data: californiaHPWH,
                    borderColor: '#ef4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    borderWidth: 2,
                    borderDash: [5, 5],
                    fill: false,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    tension: 0.4
                  }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text: 'Average Cost of Installation of HVAC and HPWH Systems',
                    font: {
                      size: 16,
                      weight: 'bold'
                    }
                  },
                  subtitle: {
                    display: true,
                    text: 'Among TECH Participating Contractors',
                    font: {
                      size: 14
                    }
                  },
                  legend: {
                    display: true,
                    position: 'bottom'
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const value = context.parsed.y;
                        return `${context.dataset.label}: $${value.toLocaleString()}`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Year'
                    }
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Cost of Installation ($)'
                    },
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `$${Number(value).toLocaleString()}`
                    }
                  }
                }
              }
            });
          }
        }

      } catch (err) {
        console.error('Error fetching heat pump cost data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load heat pump cost data');
      } finally {
        setLoading(false);
      }
    };

    fetchHeatPumpData();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location, geofips]);

  if (loading) {
    return <div>Loading heat pump cost data...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ height: '450px', width: '100%' }}>
      <canvas ref={chartRef} />
    </div>
  );
};
